import { IconX } from '@tabler/icons-react'
import { Tooltip } from 'antd'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'

import { Avatar } from './Avatar'

import convertEnumToString from '@/utils/convertEnumToString'
import { getAdminUserName, getWorkerName } from '@/utils/getName'
import { routes } from '@/utils/routes'

import { AssigneeType } from 'types/api.graphql'
import { AssigneeLike } from 'types/vagueTypes'

const AVATAR_SIZE = 24

type PillProps = {
  inline?: boolean
  white?: boolean
}

const Pill = styled.div<PillProps>`
  align-self: center;
  ${({ theme }) => theme.font.label02}
  color: ${({ theme }) => theme.colour.secondary.base};
  background-color: ${({ theme }) => theme.colour.neutral.surface};
  height: calc(${AVATAR_SIZE}px + 8px);
  padding: 12px 12px 12px 4px;
  border-radius: ${({ theme }) => theme.borderRadius.max};
  display: ${({ inline }) => (inline ? 'inline-flex' : 'flex')};
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;

  ${({ white, theme }) =>
    white &&
    css`
      background-color: ${theme.colour.neutral.white};
      border: 1px solid ${theme.colour.secondary._50};
    `};

  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};

  &:hover .bp-assignee-name {
    text-decoration: ${({ onClick }) => (onClick ? 'underline' : 'none')};
  }
`

type Props = {
  id: string
  name: string
  type: string
  onClear?: (id: string) => void
  handleClick?: () => void
  white?: boolean
  inline?: boolean
  initialsOnly?: boolean
  noTooltip?: boolean
  bordered?: boolean
}

export const AssigneePill: React.FC<Props> = ({
  id,
  name,
  type,
  onClear,
  handleClick,
  white,
  inline,
  initialsOnly,
  noTooltip = false,
  bordered = false,
}) => {
  if (initialsOnly) {
    return (
      <Tooltip
        title={
          !noTooltip ? `${name} (${convertEnumToString(type)})` : undefined
        }
      >
        <Avatar name={name} bordered={bordered} />
      </Tooltip>
    )
  }

  return (
    <Pill white={white} inline={inline} onClick={handleClick}>
      <Avatar name={name} bordered={bordered} />
      <Tooltip
        title={!noTooltip ? convertEnumToString(type) : undefined}
        className="bp-assignee-name"
      >
        {name}
      </Tooltip>
      {onClear && (
        <IconX
          size={16}
          style={{ cursor: 'pointer' }}
          onClick={() => onClear(id)}
        />
      )}
    </Pill>
  )
}

type SmartAssigneePillProps = Partial<Props> & {
  assignee: AssigneeLike
  autoLink?: boolean
}

export const SmartAssigneePill: React.FC<SmartAssigneePillProps> = ({
  assignee,
  autoLink = true,
  ...props
}) => {
  const { push } = useRouter()

  if (!assignee) return null

  if (assignee.worker) {
    return (
      <AssigneePill
        key={assignee.worker.id}
        id={assignee.worker.id}
        name={getWorkerName(assignee.worker)}
        type={AssigneeType.Worker}
        handleClick={
          autoLink
            ? () => push(routes.workers.view(assignee.worker.id))
            : undefined
        }
        {...props}
      />
    )
  }

  if (assignee.subcontractor) {
    return (
      <AssigneePill
        key={assignee.subcontractor.id}
        id={assignee.subcontractor.id}
        name={assignee.subcontractor.company.name}
        type={AssigneeType.Subcontractor}
        handleClick={
          autoLink
            ? () => push(routes.subcontractors.view(assignee.subcontractor.id))
            : undefined
        }
        {...props}
      />
    )
  }

  if (assignee.adminUser) {
    return (
      <AssigneePill
        key={assignee.adminUser.id}
        id={assignee.adminUser.id}
        name={getAdminUserName(assignee.adminUser)}
        type={AssigneeType.AdminUser}
        {...props}
      />
    )
  }

  return null
}
